import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, At, PaperPlaneTilt, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function MagicLinkPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefill = params.get("email") || (typeof window !== "undefined" && sessionStorage.getItem("voxly:lastEmail")) || "";
  const [email, setEmail] = useState(prefill);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/magic-link/send", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error("Too many attempts. Try again in a few minutes.");
      else toast.error(err?.response?.data?.detail || "Couldn't send the link. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="magic-link-page">
      <div className="mx-auto max-w-md min-h-screen sm:border-x sm:border-white/5 px-6 py-10 flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <Logo size={36} />
          <Link to="/" data-testid="magic-back" className="p-2 text-muted-foreground hover:text-foreground rounded-full">
            <ArrowLeft size={18} />
          </Link>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="flex-1 flex flex-col">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 text-primary text-[10px] font-mono uppercase tracking-widest w-fit">
              <Sparkle size={12} weight="fill" /> No password needed
            </div>
            <h1 className="mt-4 font-heading text-4xl font-black tracking-tight">
              Sign in with<br /><span className="text-primary">one tap.</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Type your email. We'll send a magic link — tap it from your inbox and you're in. No password to remember, ever.
            </p>

            <label className="mt-8 block">
              <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Email</span>
              <div className="mt-1.5 relative">
                <At size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="magic-email-input"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-secondary border border-white/10 rounded-2xl pl-10 pr-3 py-3.5 text-base focus:outline-none focus:border-primary"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting || !email}
              data-testid="magic-submit"
              className="mt-6 w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold flex items-center justify-center gap-2 glow-hover disabled:opacity-40"
            >
              <PaperPlaneTilt size={18} weight="fill" />
              {submitting ? "Sending…" : "Email me a magic link"}
            </button>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              Prefer a password? <Link to="/" className="text-primary font-semibold">Back to sign in</Link>
            </p>

            <div className="mt-auto pt-10 text-[11px] text-muted-foreground/80">
              <p className="font-mono uppercase tracking-widest mb-1">How it works</p>
              <p>Magic links expire in 15 minutes and can only be used once. Safer than a stolen password.</p>
            </div>
          </form>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="magic-success">
            <CheckCircle size={64} weight="fill" className="text-emerald-500" />
            <h1 className="mt-6 font-heading text-3xl font-black tracking-tight">Check your inbox</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xs">
              We just sent a one-tap sign-in link to <span className="text-foreground font-semibold">{email}</span>. Open it on this device. Valid for 15 minutes.
            </p>
            <p className="mt-4 text-[11px] text-muted-foreground/80 max-w-xs">
              Check Spam/Promotions if you don't see it. Sender: <span className="text-foreground">Voxly</span>.
            </p>
            <button
              onClick={() => navigate("/", { replace: true })}
              data-testid="magic-return-login"
              className="mt-8 px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
